import styled from '@emotion/styled';

import { typographyVariantStyles } from '@/design-system/typography-variants';

import { CHANGED_FIELD_COLOR_VAR, DISABLED_EXCLUSIONS, focusRing } from '../../interaction-states';
import { forHover, forMobile } from '../../responsive';
import type { TableColumnAlign, TableColumnSize, TablePinSide, TableVariant } from './types';

export const cellHeights: Record<TableColumnSize, number> = {
  lg: 64,
  md: 54,
  sm: 40,
};

export const TABLE_COLUMN_GAP = 24;

const justifyContentMap: Record<TableColumnAlign, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// ─── Table Structure ──────────────────────────────────────────────────────────

export const StyledTableWrapper = styled('div')({
  position: 'relative',
  width: '100%',
});

export const StyledTableRoot = styled('div', {
  shouldForwardProp: (p) => !['$containQueries', '$maxHeight'].includes(p),
})<{ $containQueries?: boolean; $maxHeight?: number | string }>(
  ({ theme, $containQueries, $maxHeight }) => ({
    width: '100%',
    overflowX: 'auto',
    ...($maxHeight !== undefined && { maxHeight: $maxHeight, overflowY: 'auto' }),
    ...($containQueries && { containerType: 'inline-size' as const }),

    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.colors.background.overlay,
      borderRadius: 8,
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.colors.background.overlay} transparent`,
  }),
);

export const StyledScrollEndShadow = styled('div', {
  shouldForwardProp: (p) => p !== '$visible',
})<{ $visible: boolean }>(({ $visible }) => ({
  display: 'none',
  [forMobile]: {
    display: 'block',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: 32,
    pointerEvents: 'none',
    background: 'linear-gradient(to left, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0))',
    opacity: $visible ? 1 : 0,
    transition: 'opacity 0.15s ease',
  },
}));

export const StyledTableRow = styled('div', {
  shouldForwardProp: (p) => !['$gridTemplateColumns', '$sticky'].includes(p),
})<{ $gridTemplateColumns: string; $sticky?: boolean }>(
  ({ theme, $gridTemplateColumns, $sticky }) => ({
    display: 'grid',
    gridTemplateColumns: $gridTemplateColumns,
    gap: TABLE_COLUMN_GAP,
    ...($sticky && {
      position: 'sticky' as const,
      top: 0,
      zIndex: 3,
      backgroundColor: `var(--table-surface, ${theme.colors.background.canvas})`,
    }),
  }),
);

const ROW_SURFACE_SELECTOR = '[data-row-surface]';
const PINNED_CELL_SELECTOR = '[data-pinned]';

export const StyledTableDataRow = styled(StyledTableRow, {
  shouldForwardProp: (p) => !['$isLink', '$variant', '$roundedScrollEdges'].includes(p),
})<{ $isLink?: boolean; $variant?: TableVariant; $roundedScrollEdges?: boolean }>(({
  theme,
  $isLink = false,
  $variant = 'default',
  $roundedScrollEdges = false,
}) => {
  const restingColor = $variant === 'plain' ? 'transparent' : theme.colors.background.raised;

  /**
   * The brand tint is an opaque background image, so the hover background colour under it never
   * shows. A highlighted row instead lays the translucent hover overlay over the tint, as an inset
   * shadow that transitions with the rest of the row and keeps the leading strip on top.
   */
  const highlightHover = {
    boxShadow: `inset 0 0 0 100vmax ${theme.colors.state.hover}`,
  };

  /**
   * The pressed overlay is translucent. Laid on the row and again on a pinned cell sitting over it,
   * it stacks, and over a backdrop it shows whatever is behind the table. So the row (or its
   * surface) and its pinned cells take one opaque fill from the same rule: the table surface with
   * the overlay on top, under the brand tint on a highlighted row.
   */
  const pressedOverlay = `linear-gradient(${theme.colors.state.hoverStrong}, ${theme.colors.state.hoverStrong})`;
  const brandTint = `linear-gradient(${theme.colors.brand.subtle}, ${theme.colors.brand.subtle})`;
  const pressed = (surface?: string) => {
    const targets = (state: string) =>
      `${surface ? `&${state} > ${surface}` : `&${state}`}, &${state} > ${PINNED_CELL_SELECTOR}`;

    return {
      [targets(`:active${DISABLED_EXCLUSIONS}`)]: {
        backgroundColor: `var(--table-surface, ${theme.colors.background.canvas})`,
        backgroundImage: pressedOverlay,
      },
      [targets(`[data-highlight="true"]:active${DISABLED_EXCLUSIONS}`)]: {
        backgroundImage: `${brandTint}, ${pressedOverlay}`,
      },
    };
  };

  const highlightStrip = (radius: number) => ({
    content: '""',
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    left: 0,
    width: Math.max(radius, 2),
    boxSizing: 'border-box' as const,
    borderLeft: `2px solid ${theme.colors.brand.solid}`,
    borderRadius: `${radius}px 0 0 ${radius}px`,
    pointerEvents: 'none' as const,
  });

  /**
   * In the scrolling variant the visible row is the sticky `[data-row-surface]`, not the row box:
   * the row spans the whole scroll width, so an outline on it is drawn largely out of view and the
   * surface paints over whatever is left, leaving stubs at the two edges. The focus ring below
   * already targets the surface for the same reason. The plain variant has no surface — there the
   * row paints its own background and owns the outline.
   */
  const stateOutlines = (onSurface: boolean) => {
    const radius = $variant === 'plain' ? 0 : 16;
    const on = (state: string) =>
      onSurface ? `&[${state}] > ${ROW_SURFACE_SELECTOR}` : `&[${state}]`;

    return {
      [on('data-changed="true"')]: {
        outline: `1px solid var(${CHANGED_FIELD_COLOR_VAR}, ${theme.colors.orange.solid})`,
        outlineOffset: -1,
      },

      /**
       * The viewer's own rows, e.g. offers Gamma posted. A tint plus a brand leading strip, the
       * same device as the attention strip below, so the rows stay findable without reading as a
       * warning. The tint is layered over the resting surface so pinned cells match the row.
       */
      [on('data-highlight="true"')]: {
        backgroundImage: `linear-gradient(${theme.colors.brand.subtle}, ${theme.colors.brand.subtle})`,
        ...(onSurface ? {} : { position: 'relative' as const }),

        '&::before': {
          ...highlightStrip(radius),
          // Under pinned cells (2) and a sticky header (3); a start-pinned cell draws its own strip.
          zIndex: 1,
        },
      },
      [`&[data-highlight="true"] > ${PINNED_CELL_SELECTOR}`]: {
        backgroundImage: `linear-gradient(${theme.colors.brand.subtle}, ${theme.colors.brand.subtle})`,
      },
      // A start-pinned cell stays put while the row scrolls away under it; it carries the strip.
      [`&[data-highlight="true"] > [data-pinned="start"]::before`]: highlightStrip(radius),

      /**
       * A leading strip rather than a full ring: an attention row is routine work, not a failure,
       * and several ringed rows at once read as a broken table. The cell that raised the state
       * already carries the wording, so this only has to make the row findable while scanning.
       * Same device the pipeline card uses for its tone. Sits on the sticky surface, so it stays
       * at the visible edge while the table scrolls sideways.
       */
      [on('data-alert="true"')]: {
        // No `position` here: the surface is already `sticky`, and relative would cancel that.
        ...(onSurface ? {} : { position: 'relative' as const }),

        '&::before': {
          content: '""',
          position: 'absolute' as const,
          top: 0,
          bottom: 0,
          left: 0,
          width: 3,
          borderRadius: `${radius}px 0 0 ${radius}px`,
          backgroundColor: theme.colors.error.solid,
          pointerEvents: 'none' as const,
        },
      },
    };
  };

  if ($roundedScrollEdges) {
    return {
      cursor: $isLink ? 'pointer' : 'default',
      borderRadius: 16,
      ...stateOutlines(true),

      '& > *': {
        gridRow: 1,
      },

      [`& > ${ROW_SURFACE_SELECTOR}`]: {
        backgroundColor: restingColor,
      },

      ...($isLink && {
        [forHover]: {
          [`&:hover${DISABLED_EXCLUSIONS} > ${ROW_SURFACE_SELECTOR}, &:hover${DISABLED_EXCLUSIONS} > ${PINNED_CELL_SELECTOR}`]:
            {
              backgroundColor: theme.colors.state.hoverRaised,
            },
          [`&[data-highlight="true"]:hover${DISABLED_EXCLUSIONS} > ${ROW_SURFACE_SELECTOR}, &[data-highlight="true"]:hover${DISABLED_EXCLUSIONS} > ${PINNED_CELL_SELECTOR}`]:
            highlightHover,
        },

        ...pressed(ROW_SURFACE_SELECTOR),

        [`&:focus-visible${DISABLED_EXCLUSIONS} > ${ROW_SURFACE_SELECTOR}`]: {
          outline: `2px solid ${theme.colors.border.focus}`,
          outlineOffset: -2,
        },

        '&:focus-visible': {
          outline: 'none',
        },
      }),
    };
  }

  return {
    backgroundColor: restingColor,
    cursor: $isLink ? 'pointer' : 'default',
    borderRadius: $variant === 'plain' ? 0 : 16,
    transition: theme.motion.hover,
    ...stateOutlines(false),

    ...($isLink && {
      [forHover]: {
        [`&:hover${DISABLED_EXCLUSIONS}, &:hover${DISABLED_EXCLUSIONS} > ${PINNED_CELL_SELECTOR}`]:
          {
            backgroundColor: theme.colors.state.hoverRaised,
          },
        [`&[data-highlight="true"]:hover${DISABLED_EXCLUSIONS}, &[data-highlight="true"]:hover${DISABLED_EXCLUSIONS} > ${PINNED_CELL_SELECTOR}`]:
          highlightHover,
      },

      ...pressed(),

      ...focusRing(theme),
    }),
  };
});

export const StyledRowSurface = styled('span', {
  shouldForwardProp: (p) => p !== '$variant',
})<{ $variant?: TableVariant }>(({ theme, $variant = 'default' }) => ({
  display: 'block',
  gridArea: '1 / 1 / 2 / -1',
  position: 'sticky',
  left: 0,
  width: '100cqi',
  height: '100%',
  borderRadius: $variant === 'plain' ? 0 : 16,
  transition: theme.motion.hover,
  pointerEvents: 'none',
  zIndex: 0,
}));

// Holds a row together with its expanded content so both read as one surface:
// the group owns the background and the 16px radius, the row inside stays
// transparent-equivalent and the expanded content is clipped to the same corners.
export const StyledExpandableRowGroup = styled('div', {
  shouldForwardProp: (p) => p !== '$variant',
})<{ $variant?: TableVariant }>(({ theme, $variant = 'default' }) => ({
  backgroundColor: $variant === 'plain' ? 'transparent' : theme.colors.background.raised,
  borderRadius: $variant === 'plain' ? 0 : 16,
  overflow: 'hidden',
}));

export const StyledEmptyState = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

export const StyledTableBody = styled('div', {
  shouldForwardProp: (p) => p !== '$variant',
})<{ $variant?: TableVariant }>(({ $variant = 'default' }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: $variant === 'plain' ? 0 : 8,
  gap: $variant === 'plain' ? 0 : 8,
}));

// ─── Header Cell ─────────────────────────────────────────────────────────────

export const StyledHeaderCell = styled('div', {
  shouldForwardProp: (p) => !['$align', '$sortable', '$pin', '$anchorEnd'].includes(p),
})<{
  $align?: TableColumnAlign;
  $sortable?: boolean;
  $pin?: TablePinSide;
  $anchorEnd?: boolean;
}>(({ theme, $align = 'left', $sortable, $pin, $anchorEnd }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  height: 40,
  justifyContent: justifyContentMap[$align],
  boxSizing: 'border-box',
  overflow: 'hidden',
  minWidth: 0,
  // Sits in the final track, past the filler, so it stays flush right.
  ...($anchorEnd && { gridColumnStart: -2, gridColumnEnd: -1 }),
  ...($sortable && {
    cursor: 'pointer',
    userSelect: 'none' as const,
    '&:hover [data-sort-icon]': {
      opacity: 1,
    },
  }),
  ...($pin && {
    position: 'sticky' as const,
    ...($pin === 'start' ? { left: 0 } : { right: 0 }),
    zIndex: 2,
    backgroundColor: `var(--table-surface, ${theme.colors.background.canvas})`,
  }),

  '&:first-of-type': {
    paddingLeft: 12,
  },
  '&:last-of-type': {
    paddingRight: 12,
  },
}));

export const StyledHeaderLabel = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body4,
  fontWeight: 500,
  color: theme.colors.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const StyledHeaderTooltipTrigger = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginLeft: 2,
  cursor: 'default',
});

export const StyledSortIconWrapper = styled('span', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active: boolean }>(({ theme, $active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: $active ? theme.colors.white[100] : theme.colors.white[40],
  opacity: $active ? 1 : 0.8,
  transition: 'color 0.15s ease, opacity 0.15s ease',
  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

// ─── Data Cell ────────────────────────────────────────────────────────────────

export const StyledDataCell = styled('div', {
  shouldForwardProp: (p) => !['$align', '$size', '$pin', '$anchorEnd', '$columnStart'].includes(p),
})<{
  $align?: TableColumnAlign;
  $size?: TableColumnSize;
  $pin?: TablePinSide;
  $anchorEnd?: boolean;
  $columnStart?: number;
}>(({ theme, $align = 'left', $size = 'md', $pin, $anchorEnd, $columnStart }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: justifyContentMap[$align],
  position: 'relative',
  gap: 8,
  height: cellHeights[$size],
  boxSizing: 'border-box',
  minWidth: 0,
  overflow: 'hidden',
  ...($columnStart !== undefined && { gridColumnStart: $columnStart }),
  // Sits in the final track, past the filler, so it stays flush right.
  ...($anchorEnd && { gridColumnStart: -2, gridColumnEnd: -1 }),
  // A pinned cell paints its own share of the row, so it takes the row's hover transition and the
  // whole row changes colour at the same moment.
  ...($pin === 'end' && {
    position: 'sticky' as const,
    right: 0,
    zIndex: 2,
    backgroundColor: theme.colors.background.raised,
    borderLeft: `1px solid ${theme.colors.border.default}`,
    transition: theme.motion.hover,
  }),
  ...($pin === 'start' && {
    position: 'sticky' as const,
    left: 0,
    zIndex: 2,
    backgroundColor: theme.colors.background.raised,
    borderRight: `1px solid ${theme.colors.border.default}`,
    transition: theme.motion.hover,
  }),

  '&:first-of-type': {
    paddingLeft: 12,
    borderRadius: $pin === 'start' ? '16px 0 0 16px' : undefined,
  },
  '&:last-of-type': {
    paddingRight: 12,
    borderRadius: $pin === 'end' ? '0 16px 16px 0' : 'initial',
  },
}));
